import { ExperimentTag } from "@/http/api-types";

// Typing a single character matches almost every tag, so the picker waits for two before
// it queries. Below that it keeps showing the recently used tags instead.
export const tagSearchMinLength = 2;
// Matches the debounce the experiments list filter uses, so both search fields feel alike.
export const tagSearchDebounceMs = 300;
// How many of the most recently used tags the picker offers before the user types.
export const recentTagSuggestionCount = 5;

/**
 * Mirrors the backend's normalization: trim the ends and collapse internal whitespace
 * runs, so the name previewed in the "Create" row is the name that gets stored.
 */
export function normalizeTagName(value: string): string {
  return value.trim().split(/\s+/).filter(Boolean).join(" ");
}

/**
 * The term to send to the backend: empty until the input reaches the minimum length, so
 * the caller can key its request on this and skip the short-input round trips entirely.
 */
export function tagSearchTerm(input: string): string {
  const normalized = normalizeTagName(input);
  return normalized.length >= tagSearchMinLength ? normalized : "";
}

function sameTagName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function isTagSelected(selectedNames: string[], name: string): boolean {
  return selectedNames.some(selected => sameTagName(selected, name));
}

/**
 * Suggestions to render: tags already on the experiment are dropped, and with no search
 * term the list is capped at the most recent few. The backend returns tags
 * most-recently-used first, so slicing the head is what "recent tags" means.
 */
export function tagSuggestions(tags: ExperimentTag[] | undefined, selectedNames: string[], search: string) {
  const available = (tags || []).filter(tag => !isTagSelected(selectedNames, tag.name));
  return search ? available : available.slice(0, recentTagSuggestionCount);
}

/**
 * Whether to offer creating the typed name as a new tag.
 *
 * `suggestionsMatchInput` guards against offering "Create ranking" while the debounced
 * request for "ranking" is still in flight and the visible suggestions still answer an
 * earlier keystroke. Below the search minimum there is nothing in flight, so a short name
 * can always be created — the backend resolves names case-insensitively and reuses an
 * existing tag rather than duplicating it.
 */
export function canCreateTag(
  input: string,
  suggestions: ExperimentTag[],
  selectedNames: string[],
  suggestionsMatchInput: boolean,
): boolean {
  const name = normalizeTagName(input);
  if (!name || isTagSelected(selectedNames, name)) {
    return false;
  }
  if (name.length >= tagSearchMinLength && !suggestionsMatchInput) {
    return false;
  }
  return !suggestions.some(tag => sameTagName(tag.name, name));
}

/** Adds a tag name, keeping the list free of case-insensitive duplicates. */
export function addTagName(selectedNames: string[], name: string): string[] {
  const normalized = normalizeTagName(name);
  if (!normalized || isTagSelected(selectedNames, normalized)) {
    return selectedNames;
  }
  return [...selectedNames, normalized];
}

/** Removes a tag name, matching case-insensitively like the backend does. */
export function removeTagName(selectedNames: string[], name: string): string[] {
  return selectedNames.filter(selected => !sameTagName(selected, name));
}

/** True when two tag name lists hold the same tags, ignoring order and case. */
export function haveSameTagNames(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = a.map(name => name.toLowerCase()).sort();
  const sortedB = b.map(name => name.toLowerCase()).sort();
  return sortedA.every((name, index) => name === sortedB[index]);
}
